import {
  Anchor,
  Box,
  Burger,
  Center,
  Collapse,
  Divider,
  Drawer,
  Group,
  HoverCard,
  Image,
  Menu,
  rem,
  ScrollArea,
  Select,
  SimpleGrid,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { useDisclosure } from "@mantine/hooks";
import {
  IconChevronDown,
  IconChevronUp,
  IconCirclePlus,
  IconLogout,
  IconSearch,
  IconProps,
} from "@tabler/icons-react";
import { Fragment, useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useOidc } from "@axa-fr/react-oidc";
import {
  setSelectedOrganizationId,
  useSelectedOrganizationId,
} from "../../../../../business/OrganizationId";
import { gql } from "@apollo/client";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useQuery } from "@apollo/client/react";
import { Organization } from "../../../../../gql/graphql";
import { useAuthenticator } from "@aws-amplify/ui-react";

const useStyles = createStyles((theme) => ({
  link: {
    display: "flex",
    alignItems: "center",
    height: "100%",
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    textDecoration: "none",
    color: theme.white,
    fontWeight: 500,
    fontSize: theme.fontSizes.sm,

    [`@media (max-width: ${theme.breakpoints.sm})`]: {
      height: rem(42),
      display: "flex",
      alignItems: "center",
      width: "100%",
    },

    "&:hover": {
      backgroundColor: theme.colors.dark[6],
    },
  },

  subLink: {
    width: "100%",
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
    borderRadius: theme.radius.md,

    "&:hover": {
      backgroundColor: theme.colors.dark[7],
    },
  },

  dropdownFooter: {
    backgroundColor: theme.colors.dark[7],
    margin: `calc(${theme.spacing.md} * -1)`,
    marginTop: theme.spacing.sm,
    padding: `${theme.spacing.md} calc(${theme.spacing.md} * 2)`,
    paddingBottom: theme.spacing.xl,
    borderTop: `${rem(1)} solid ${theme.colors.dark[5]}`,
  },

  hiddenMobile: {
    [`@media (max-width: ${theme.breakpoints.sm})`]: {
      display: "none",
    },
  },

  hiddenDesktop: {
    [`@media (min-width: ${theme.breakpoints.sm})`]: {
      display: "none",
    },
  },

  user: {
    color: theme.colors.dark[0],
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.radius.sm,
    transition: "background-color 100ms ease",

    "&:hover": {
      backgroundColor: theme.colors.dark[8],
    },

    [`@media (max-width: ${theme.breakpoints.xs})`]: {
      display: "none",
    },
  },

  burger: {
    [`@media (min-width: ${theme.breakpoints.xs})`]: {
      display: "none",
    },
  },

  userActive: {
    backgroundColor: theme.colors.dark[8],
  },
}));

export interface ServiceLinkItem {
  icon: React.ComponentType<IconProps>;
  title: string;
  description: string;
  path: string;
  disabled?: boolean;
  color?: string;
}

export interface HeaderLink {
  title: string;
  path?: string;
  children?: ServiceLinkItem[];
}

const UserMenu = () => {
  const { logout } = useOidc("wp");
  const { signOut } = useAuthenticator();
  const [userMenuOpened, setUserMenuOpened] = useState(false);
  const { classes, cx } = useStyles();

  return (
    <Menu
      transitionProps={{ transition: "pop-top-right" }}
      onClose={() => setUserMenuOpened(false)}
      onOpen={() => setUserMenuOpened(true)}
    >
      <Menu.Target>
        <UnstyledButton
          className={cx(classes.user, {
            [classes.userActive]: userMenuOpened,
          })}
        >
          <Group gap={7}>
            <Text fw={500} size="sm" style={{ lineHeight: 1 }} mr={3}>
              Account
            </Text>
            <IconChevronDown size={12} stroke={1.5} />
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {/*<Menu.Item
          leftSection={
            <IconHeart size={14} color={theme.colors.red[6]} stroke={1.5} />
          }
        >
          Liked posts
        </Menu.Item>
        <Menu.Item
          leftSection={
            <IconStar
              size={14}
              color={theme.colors.yellow[6]}
              stroke={1.5}
            />
          }
        >
          Saved posts
        </Menu.Item>
        <Menu.Item
          leftSection={
            <IconMessage
              size={14}
              color={theme.colors.blue[6]}
              stroke={1.5}
            />
          }
        >
          Your comments
        </Menu.Item>

        <Menu.Label>Settings</Menu.Label>
        <Menu.Item leftSection={<IconSettings size={14} stroke={1.5} />}>
          Account settings
        </Menu.Item>
        <Menu.Item leftSection={<IconSwitchHorizontal size={14} stroke={1.5} />}>
          Change account
        </Menu.Item>*/}
        <Menu.Item
          leftSection={<IconLogout size={14} stroke={1.5} />}
          onClick={() => logout().then(() => signOut())}
        >
          Logout
        </Menu.Item>

        {/*<Menu.Divider />

        <Menu.Label>Danger zone</Menu.Label>
        <Menu.Item leftSection={<IconPlayerPause size={14} stroke={1.5} />}>
          Pause subscription
        </Menu.Item>
        <Menu.Item color="red" leftSection={<IconTrash size={14} stroke={1.5} />}>
          Delete account
        </Menu.Item>*/}
      </Menu.Dropdown>
    </Menu>
  );
};

export function HeaderMegaMenu({ links }: { links: HeaderLink[] }) {
  const selectedOrganizationId = useSelectedOrganizationId();
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] =
    useDisclosure(false);
  const [selectedOrganization, setSelectedOrganization] = useState<{
    name: string;
    id: string;
  }>();
  const [linksOpened, { toggle: toggleLinks }] = useDisclosure(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { classes, theme } = useStyles();
  const navigate = useNavigate();
  const { data } = useQuery(
    gql`
      query {
        userOrganizations {
          id
          name
        }
      }
    ` as TypedDocumentNode<{ userOrganizations: Organization[] }>,
  );

  useEffect(() => {
    if (data) {
      if (!selectedOrganizationId && data?.userOrganizations?.length > 0) {
        setSelectedOrganizationId(data?.userOrganizations[0].id);
        return;
      }

      if (data?.userOrganizations?.length === 0) {
        navigate("/organizations/new");
        return;
      }

      const selectedOrganization = data?.userOrganizations?.find(
        (org: any) => org.id === selectedOrganizationId,
      );
      if (!selectedOrganization) {
        setSelectedOrganizationId(null);
      } else {
        setSelectedOrganization(selectedOrganization);
      }
    }
  }, [data]);

  const selectOrganization = (id: string) => {
    setSelectedOrganizationId(id);
  };

  return (
    <>
      <div
        style={{
          height: 60,
          paddingLeft: rem(16),
          paddingRight: rem(16),
          display: "flex",
          alignItems: "center",
        }}
      >
        <Group justify="space-between" style={{ width: "100%" }}>
          <Group>
            <Image src="/favicon.svg" w={30} h={30} />
            <Group ml={3} gap={4}>
              <Tooltip label="Select organization">
                <Select
                  data={
                    data?.userOrganizations?.map((organization: any) => ({
                      value: organization.id as string,
                      label: organization.name as string,
                    })) || []
                  }
                  value={selectedOrganizationId}
                  onChange={(value) => selectOrganization(value ?? "")}
                />
              </Tooltip>
              <Tooltip label="Create organization">
                <Anchor mt={5} component={NavLink} to="/organizations/new">
                  <IconCirclePlus size={20} />
                </Anchor>
              </Tooltip>
              <Group
                style={{ height: "100%" }}
                gap={0}
                className={classes.hiddenMobile}
              >
                {links.map(
                  (item, idx) =>
                    (item.path && (
                      <Anchor
                        className={classes.link}
                        component={NavLink}
                        to={item.path}
                        key={idx}
                      >
                        {item.title}
                      </Anchor>
                    )) ||
                    (item.children && (
                      <HoverCard key={idx}>
                        <HoverCard.Target>
                          <Anchor className={classes.link}>
                            <Center inline>
                              <Box component="span" mr={5}>
                                {item.title}
                              </Box>
                              <IconChevronDown
                                size={16}
                                color={theme.primaryColor}
                              />
                            </Center>
                          </Anchor>
                        </HoverCard.Target>

                        <HoverCard.Dropdown style={{ overflow: "hidden" }}>
                          <Group justify="space-between" px="md">
                            <Text fw={500}>{item.title}</Text>
                            <TextInput
                              placeholder="Search"
                              leftSection={<IconSearch size={16} />}
                              onChange={(value) =>
                                setSearchQuery(value.target.value.toLowerCase())
                              }
                            />
                          </Group>

                          <Divider my="sm" mx="-md" color={"dark.5"} />

                          <ScrollArea h={200} mx="-md">
                            <SimpleGrid cols={2} spacing={0}>
                              {item.children
                                .filter(
                                  (item) =>
                                    item.title
                                      .toLowerCase()
                                      .includes(searchQuery) ||
                                    item.description
                                      .toLowerCase()
                                      .includes(searchQuery),
                                )
                                .map((item, idx) => (
                                  <UnstyledButton
                                    className={classes.subLink}
                                    key={idx}
                                    component={Link}
                                    to={item.path}
                                    onClick={(e) =>
                                      item.disabled && e.preventDefault()
                                    }
                                  >
                                    <Group wrap="nowrap" align="flex-start">
                                      <item.icon
                                        size={20}
                                        color={
                                          item.disabled
                                            ? theme.colors.gray[7]
                                            : theme.primaryColor
                                        }
                                        style={{
                                          flexShrink: 0,
                                          marginTop: rem(2),
                                        }}
                                      />
                                      <div>
                                        <Text size="sm" fw={500}>
                                          {item.title}
                                        </Text>
                                        <Text size="xs" c="dimmed">
                                          {item.description}
                                        </Text>
                                      </div>
                                    </Group>
                                  </UnstyledButton>
                                ))}
                            </SimpleGrid>
                          </ScrollArea>
                        </HoverCard.Dropdown>
                      </HoverCard>
                    )),
                )}
              </Group>
            </Group>
          </Group>

          <div className={classes.hiddenMobile}>
            <UserMenu />
          </div>

          <Burger
            opened={drawerOpened}
            onClick={toggleDrawer}
            className={classes.hiddenDesktop}
          />
        </Group>
      </div>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size="100%"
        padding="md"
        title="Navigation"
        className={classes.hiddenDesktop}
        zIndex={1000000}
      >
        <ScrollArea h={`calc(100vh - ${rem(60)})`} mx="-md">
          <Divider my="sm" color={"dark.5"} />

          {links.map(
            (item, idx) =>
              (item.path && (
                <Anchor
                  className={classes.link}
                  key={idx}
                  component={NavLink}
                  to={item.path}
                  onClick={() => {
                    closeDrawer();
                  }}
                >
                  {item.title}
                </Anchor>
              )) ||
              (item.children && (
                <Fragment key={idx}>
                  <UnstyledButton
                    className={classes.link}
                    onClick={toggleLinks}
                  >
                    <Center inline>
                      <Box component="span" mr={5}>
                        {item.title}
                      </Box>
                      {(linksOpened && (
                        <IconChevronUp size={16} color={theme.primaryColor} />
                      )) || (
                        <IconChevronDown size={16} color={theme.primaryColor} />
                      )}
                    </Center>
                  </UnstyledButton>
                  <Collapse in={linksOpened}>
                    {item.children.map((item, idx) => (
                      <UnstyledButton
                        className={classes.subLink}
                        key={idx}
                        component={NavLink}
                        to={item.path}
                        onClick={() => {
                          closeDrawer();
                        }}
                      >
                        <Group wrap="nowrap" align="flex-start">
                          <item.icon
                            size={20}
                            color={theme.primaryColor}
                            style={{ flexShrink: 0, marginTop: rem(2) }}
                          />
                          <div>
                            <Text size="sm" fw={500}>
                              {item.title}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {item.description}
                            </Text>
                          </div>
                        </Group>
                      </UnstyledButton>
                    ))}
                  </Collapse>
                </Fragment>
              )),
          )}

          <Divider my="sm" color={"dark.5"} />

          <UserMenu />
        </ScrollArea>
      </Drawer>
    </>
  );
}
